import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Slider,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  TextField,
  IconButton,
  Paper,

  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,


} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import FastForwardIcon from '@mui/icons-material/FastForward';
import FastRewindIcon from '@mui/icons-material/FastRewind';

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase } from '../lib/supabaseClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

interface LocationHistory {
  id: number;
  user_id: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  sharing_id?: string;
}

interface UserInfo {
  id: string;
  username: string;
  user_group: string;
}

const PADANG_COORDS = { lat: 1.2897, lng: 103.8501 };

// Add constant for valid user groups
const VALID_USER_GROUPS = [
  'OS Team',
  'Show Support',
  'Show Management (Bay)',
  'Show Management (Padang)',
  'Artiste Management',
  'Log & Finance',
  'Ops. Safety and Contingencies'
];

export default function Playback() {
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const polylineRef = useRef<L.Polyline | null>(null);

  const [users, setUsers] = useState<UserInfo[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [locationHistory, setLocationHistory] = useState<LocationHistory[]>([]);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [noDataMessage, setNoDataMessage] = useState('');

  // Add new state variables for group playback
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupUsers, setGroupUsers] = useState<UserInfo[]>([]);
  const [groupLocationHistory, setGroupLocationHistory] = useState<LocationHistory[]>([]);
  const [playbackMode, setPlaybackMode] = useState<'user' | 'group'>('user');

  // Add new refs for group markers and polylines
  const markersRef = useRef<{ [key: string]: L.Marker }>({});
  const polylinesRef = useRef<{ [key: string]: L.Polyline }>({});

  // UI state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  // Map style switcher state
  const [mapStyle, setMapStyle] = useState<'map' | 'satellite' | 'hybrid'>('map');

  // Fetch users for selection
  useEffect(() => {
    const fetchUsers = async () => {
      const { data: allUsers, error: allUsersError } = await supabase
        .from('users')
        .select('id, username, user_group');
      if (allUsersError || !allUsers) {
        setUsers([]);
        setSelectedUser('');
        return;
      }
      setUsers(allUsers);
      if (!selectedUser && allUsers.length > 0) setSelectedUser(allUsers[0].id);
      else if (allUsers.length === 0) setSelectedUser('');
    };
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update fetch location history logic to handle 'All Users' option with date filter
  useEffect(() => {
    if (selectedUser === 'all') {
      setLocationHistory([]);
      setPlaybackIndex(0);
      setNoDataMessage('');
      const fetchAllHistory = async () => {
        const { data, error } = await supabase
          .from('location_history')
          .select('*')
          .gte('timestamp', `${selectedDate} 00:00:00`)
          .lt('timestamp', `${selectedDate} 23:59:59.999`)
          .order('timestamp', { ascending: true });
        if (!error && data && data.length > 0) {
          setLocationHistory(data);
          setNoDataMessage('');
        } else {
          setLocationHistory([]);
          setNoDataMessage('No location history available for the selected date.');
        }
      };
      fetchAllHistory();
    } else if (selectedUser) {
      setLocationHistory([]);
      setPlaybackIndex(0);
      setNoDataMessage('');
      const fetchHistory = async () => {
        const { data, error } = await supabase
          .from('location_history')
          .select('*')
          .eq('user_id', selectedUser)
          .gte('timestamp', `${selectedDate} 00:00:00`)
          .lt('timestamp', `${selectedDate} 23:59:59.999`)
          .order('timestamp', { ascending: true });
        if (!error && data && data.length > 0) {
          setLocationHistory(data);
          setNoDataMessage('');
        } else {
          setLocationHistory([]);
          setNoDataMessage('No location history for this user on the selected date.');
        }
      };
      fetchHistory();
    }
  }, [selectedUser, selectedDate]);

  // Fetch users in the selected group
  useEffect(() => {
    if (!selectedGroup) {
      setGroupUsers([]);
      return;
    }
    const fetchGroupUsers = async () => {
      const { data, error } = await supabase
        .from('users')
        .select('id, username, user_group')
        .eq('user_group', selectedGroup);
      if (error) {
        console.error('Error fetching group users:', error);
        return;
      }
      setGroupUsers(data);
    };
    fetchGroupUsers();
  }, [selectedGroup]);

  // Fetch location history for the group
  useEffect(() => {
    if (groupUsers.length === 0 || !selectedDate) {
      setGroupLocationHistory([]);
      return;
    }
    const fetchGroupHistory = async () => {
      const userIds = groupUsers.map(u => u.id);
      const { data, error } = await supabase
        .from('location_history')
        .select('*')
        .in('user_id', userIds)
        .gte('timestamp', `${selectedDate} 00:00:00`)
        .lt('timestamp', `${selectedDate} 23:59:59.999`)
        .order('timestamp', { ascending: true });
      if (error) {
        console.error('Error fetching group location history:', error);
        return;
      }
      setGroupLocationHistory(data);
    };
    fetchGroupHistory();
  }, [groupUsers, selectedDate]);

  // Initialize map
  useEffect(() => {
    if (mapContainerRef.current && !mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current, { zoomControl: false }).setView([1.3521, 103.8198], 13);
      // Default tile layer
      const defaultTileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '\u00a9 OpenStreetMap contributors'
      });
      defaultTileLayer.addTo(mapRef.current);
      // Add zoom control bottom right
      L.control.zoom({ position: 'bottomright' }).addTo(mapRef.current);
      // Store tile layers for style switcher
      // mapRef.current._tileLayers = {
      //   map: defaultTileLayer,
      //   satellite: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      //     attribution: 'Tiles \u00a9 Esri',
      //   }),
      //   hybrid: L.layerGroup([
      //     L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      //       attribution: 'Tiles \u00a9 Esri',
      //     }),
      //     L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      //       attribution: '\u00a9 OpenStreetMap contributors',
      //       opacity: 0.5,
      //     })
      //   ])
      // };
      L.marker([PADANG_COORDS.lat, PADANG_COORDS.lng])
        .addTo(mapRef.current)
        .bindPopup('Destination: Padang, Singapore');
    }
    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  }, []);

  // Map style switcher state
  useEffect(() => {
    if (!mapRef.current || !(mapRef.current as any)._tileLayers) return;
    Object.values((mapRef.current as any)._tileLayers).forEach((layer: any) => {
      if (mapRef.current && mapRef.current.hasLayer(layer)) (mapRef.current as any).removeLayer(layer);
    });
    if (mapStyle === 'hybrid') {
      (mapRef.current as any)._tileLayers.hybrid.addTo(mapRef.current);
    } else {
      (mapRef.current as any)._tileLayers[mapStyle].addTo(mapRef.current);
    }
  }, [mapStyle]);

  // Find the selected user object
  const selectedUserObj = users.find(u => u.id === selectedUser);

  // Update marker and polyline on playbackIndex/history change
  useEffect(() => {
    if (!mapRef.current || locationHistory.length === 0 || !selectedUserObj) return;
    const locs = locationHistory.slice(0, playbackIndex + 1);
    const last = locs[locs.length - 1];
    if (!last) return;

    // Use an even smaller marker icon (22x22px) with color and initial based on username
    const initial = selectedUserObj.username.charAt(0).toUpperCase();
    let hash = 0;
    for (let i = 0; i < selectedUserObj.username.length; i++) {
      hash = selectedUserObj.username.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    const backgroundColor = `hsl(${hue}, 70%, 50%)`;
    const htmlString = `<div style="width:22px;height:22px;background:${backgroundColor};border-radius:50%;color:white;display:flex;align-items:center;justify-content:center;font-size:11px;user-select:none;">${initial}</div>`;
    const customIcon = L.divIcon({
      className: 'custom-icon',
      html: htmlString,
      iconSize: [22, 22],
      iconAnchor: [11, 11],
    });

    // Update or create marker
    if (markerRef.current) {
      markerRef.current.setLatLng([last.latitude, last.longitude]);
      markerRef.current.setIcon(customIcon);
    } else {
      markerRef.current = L.marker([last.latitude, last.longitude], { icon: customIcon })
        .addTo(mapRef.current)
        .bindPopup(
          `<div style='min-width:120px'><b>${selectedUserObj.username}</b><br/>${selectedUserObj.user_group}</div>`
        );
    }

    // Update or create polyline
    if (polylineRef.current) {
      polylineRef.current.setLatLngs(locs.map(l => [l.latitude, l.longitude]));
    } else {
      polylineRef.current = L.polyline(locs.map(l => [l.latitude, l.longitude]), { color: 'blue' }).addTo(mapRef.current);
    }

    // Do NOT auto-center or zoom the map during playback
    // mapRef.current.setView([last.latitude, last.longitude], 15); // <-- Disabled for user control
  }, [playbackIndex, locationHistory, selectedUserObj]);

  // Cleanup marker/polyline on user/date change
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (polylineRef.current) {
      polylineRef.current.remove();
      polylineRef.current = null;
    }
  }, [selectedUser, selectedDate]);

  // Update playback controls to use the appropriate location history based on playback mode
  useEffect(() => {
    const history = playbackMode === 'user' ? locationHistory : groupLocationHistory;
    if (playing && history.length > 0) {
      playbackIntervalRef.current = setInterval(() => {
        setPlaybackIndex(idx => {
          if (idx < history.length - 1) return idx + 1;
          setPlaying(false);
          return idx;
        });
      }, 800);
    } else if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
    return () => {
      if (playbackIntervalRef.current) clearInterval(playbackIntervalRef.current);
    };
  }, [playing, locationHistory, groupLocationHistory, playbackMode]);

  // Update current time display
  useEffect(() => {
    const history = playbackMode === 'user' ? locationHistory : groupLocationHistory;
    if (history.length > 0 && history[playbackIndex]) {
      setCurrentTime(dayjs.utc(history[playbackIndex].timestamp).tz('Asia/Singapore').format('HH:mm:ss'));
    }
  }, [playbackIndex, locationHistory, groupLocationHistory, playbackMode]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    setPlaybackIndex(Array.isArray(value) ? value[0] : value);
    setPlaying(false);
  };

  // Update map rendering for group playback
  useEffect(() => {
    if (playbackMode !== 'group' || !mapRef.current || groupLocationHistory.length === 0) return;
    // Clear existing markers and polylines
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};
    Object.values(polylinesRef.current).forEach(polyline => polyline.remove());
    polylinesRef.current = {};
    // Render markers and polylines for each user in the group
    groupUsers.forEach(user => {
      const userHistory = groupLocationHistory.filter(loc => loc.user_id === user.id);
      if (userHistory.length === 0) return;
      const locs = userHistory.slice(0, playbackIndex + 1);
      const last = locs[locs.length - 1];
      if (!last) return;
      const initial = user.username.charAt(0).toUpperCase();
      let hash = 0;
      for (let i = 0; i < user.username.length; i++) {
        hash = user.username.charCodeAt(i) + ((hash << 5) - hash);
      }
      const hue = Math.abs(hash % 360);
      const backgroundColor = `hsl(${hue}, 70%, 50%)`;
      const htmlString = `<div style="width:22px;height:22px;background:${backgroundColor};border-radius:50%;color:white;display:flex;align-items:center;justify-content:center;font-size:11px;user-select:none;">${initial}</div>`;
      const customIcon = L.divIcon({
        className: 'custom-icon',
        html: htmlString,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const marker = L.marker([last.latitude, last.longitude], { icon: customIcon })
        .addTo(mapRef.current!)
        .bindPopup(`<div style='min-width:120px'><b>${user.username}</b><br/>${user.user_group}</div>`);
      markersRef.current[user.id] = marker;
      const polyline = L.polyline(locs.map(l => [l.latitude, l.longitude]), { color: `hsl(${hue}, 70%, 50%)` }).addTo(mapRef.current!);
      polylinesRef.current[user.id] = polyline;
    });
  }, [playbackMode, groupLocationHistory, groupUsers, playbackIndex]);

  // Add after the existing marker/polyline useEffect for single user
  useEffect(() => {
    if (!mapRef.current || selectedUser !== 'all' || locationHistory.length === 0) return;

    // Remove any existing markers and polylines
    Object.values(markersRef.current).forEach(marker => marker.remove());
    markersRef.current = {};
    Object.values(polylinesRef.current).forEach(polyline => polyline.remove && polyline.remove());
    polylinesRef.current = {};

    // Group all location history by user_id
    const userHistories: { [userId: string]: LocationHistory[] } = {};
    locationHistory.forEach(loc => {
      if (!userHistories[loc.user_id]) userHistories[loc.user_id] = [];
      userHistories[loc.user_id].push(loc);
    });

    // For each user, find their latest location up to the current playbackIndex
    Object.entries(userHistories).forEach(([userId, history]) => {
      // Sort by timestamp just in case
      history.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      // Find the latest location at or before the current playbackIndex timestamp
      const currentTime = locationHistory[playbackIndex]?.timestamp;
      if (!currentTime) return;
      const userLocsUpToNow = history.filter(l => l.timestamp <= currentTime);
      if (userLocsUpToNow.length === 0) return;
      const last = userLocsUpToNow[userLocsUpToNow.length - 1];

      // Marker icon logic (same as before)
      const user = users.find(u => u.id === userId);
      const initial = user?.username?.charAt(0).toUpperCase() || '?';
      let hash = 0;
      if (user?.username) {
        for (let i = 0; i < user.username.length; i++) {
          hash = user.username.charCodeAt(i) + ((hash << 5) - hash);
        }
      }
      const hue = Math.abs(hash % 360);
      const backgroundColor = `hsl(${hue}, 70%, 50%)`;
      const htmlString = `<div style=\"width:22px;height:22px;background:${backgroundColor};border-radius:50%;color:white;display:flex;align-items:center;justify-content:center;font-size:11px;user-select:none;\">${initial}</div>`;
      const customIcon = L.divIcon({
        className: 'custom-icon',
        html: htmlString,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });

      // Add marker
      const marker = L.marker([last.latitude, last.longitude], { icon: customIcon })
        .addTo(mapRef.current!)
        .bindPopup(
          `<div style='min-width:120px'><b>${user?.username || userId}</b><br/>${user?.user_group || ''}</div>`
        );
      markersRef.current[userId] = marker;

      // Add a polyline for the user's path up to now
      const polyline = L.polyline(userLocsUpToNow.map(l => [l.latitude, l.longitude]), { color: backgroundColor }).addTo(mapRef.current!);
      polylinesRef.current[userId] = polyline;
    });

    // Instead, set the map view to Singapore
    mapRef.current.setView([1.3521, 103.8198], 11);
  }, [selectedUser, locationHistory, users, playbackIndex]);

  const handleFastForward = () => {
    const history = playbackMode === 'user' ? locationHistory : groupLocationHistory;
    const newIndex = Math.min(playbackIndex + 10, history.length - 1);
    setPlaybackIndex(newIndex);
  };

  const handleRewind = () => {
    const newIndex = Math.max(playbackIndex - 10, 0);
    setPlaybackIndex(newIndex);
  };

  const getCurrentHistory = () => {
    return playbackMode === 'user' ? locationHistory : groupLocationHistory;
  };



  return (
    <>
      {/* Map fills the entire viewport, always behind overlays */}
      <Box
        ref={mapContainerRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          background: 'transparent',
        }}
      />

      {/* Floating Back Button - align with info bar */}
      <Box sx={{ position: 'fixed', top: { xs: 64, sm: 80, md: 100 }, left: { xs: 16, sm: 32 }, zIndex: 30, display: 'flex', alignItems: 'center', height: { xs: 56, sm: 64, md: 72 } }}>
        <IconButton
          onClick={() => navigate('/live-tracking')}
          sx={{
            backgroundColor: 'rgba(255,255,255,0.85)',
            boxShadow: 3,
            borderRadius: '50%',
            width: 48,
            height: 48,
            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
          }}
        >
          <ArrowBackIcon fontSize="medium" />
        </IconButton>
      </Box>

      {/* Floating Info Bar - further down, more space for back button */}
      <Paper
        elevation={4}
        sx={{
          position: 'fixed',
          top: { xs: 64, sm: 80, md: 100 },
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 20,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 1, sm: 1.5, md: 2 },
          backgroundColor: 'rgba(255,255,255,0.98)',
          borderRadius: 999,
          boxShadow: 6,
          minWidth: { xs: 220, sm: 320, md: 420 },
          maxWidth: { xs: 340, sm: 600, md: 800 },
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 2, md: 3 },
        }}
      >
        <FormControl sx={{ minWidth: { xs: 90, sm: 120, md: 160 } }} size="small">
          <InputLabel sx={{ fontSize: { xs: 13, sm: 15, md: 18 } }}>Playback Mode</InputLabel>
          <Select
            value={playbackMode}
            label="Playback Mode"
            onChange={e => setPlaybackMode(e.target.value as 'user' | 'group')}
            sx={{ fontSize: { xs: 13, sm: 15, md: 18 }, borderRadius: 999 }}
          >
            <MenuItem value="user">User Playback</MenuItem>
            <MenuItem value="group">Group Playback</MenuItem>
          </Select>
        </FormControl>
        {playbackMode === 'user' && users.length > 0 && (
          <FormControl sx={{ minWidth: { xs: 120, sm: 180, md: 220 } }} size="small">
            <InputLabel sx={{ fontSize: { xs: 13, sm: 15, md: 18 } }}>User</InputLabel>
            <Select
              value={selectedUser}
              label="User"
              onChange={e => setSelectedUser(e.target.value)}
              sx={{
                fontSize: { xs: 15, sm: 17, md: 20 },
                borderRadius: 999,
                py: 1,
                pl: 2,
                pr: 2,
                boxShadow: 2,
                background: 'rgba(245,245,245,0.95)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: 3, boxShadow: 4, minWidth: 200 },
                },
              }}
              renderValue={val => {
                const user = users.find(u => u.id === val);
                if (!user) return '';
                const initials = user.username
                  .split(' ')
                  .map(w => w[0])
                  .join('')
                  .toUpperCase();
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#c62828',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                    }}>{initials}</Box>
                    <span>{user.username} ({user.user_group})</span>
                  </Box>
                );
              }}
            >
              <MenuItem value="all">All Users</MenuItem>
              {users.map(u => {
                const initials = u.username
                  .split(' ')
                  .map(w => w[0])
                  .join('')
                  .toUpperCase();
                return (
                  <MenuItem key={u.id} value={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#c62828',
                      color: 'white',
                      fontWeight: 700,
                      fontSize: 16,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1,
                    }}>{initials}</Box>
                    <span>{u.username} ({u.user_group})</span>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        )}
        {playbackMode === 'group' && (
          <FormControl sx={{ minWidth: { xs: 120, sm: 180, md: 220 } }} size="small">
            <InputLabel sx={{ fontSize: { xs: 13, sm: 15, md: 18 } }}>Group</InputLabel>
            <Select
              value={selectedGroup}
              label="Group"
              onChange={e => setSelectedGroup(e.target.value)}
              sx={{ fontSize: { xs: 15, sm: 17, md: 20 }, borderRadius: 999, py: 1, pl: 2, pr: 2, boxShadow: 2, background: 'rgba(245,245,245,0.95)' }}
              MenuProps={{
                PaperProps: {
                  sx: { borderRadius: 3, boxShadow: 4, minWidth: 200 },
                },
              }}
            >
              <MenuItem value="">Select a Group</MenuItem>
              {VALID_USER_GROUPS.map(group => (
                <MenuItem key={group} value={group}>{group}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          label="Date"
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontSize: { xs: 13, sm: 15, md: 18 } } }}
          sx={{ minWidth: { xs: 90, sm: 120, md: 160 }, fontSize: { xs: 13, sm: 15, md: 18 }, borderRadius: 999 }}
          size="small"
        />
      </Paper>

      {/* Playback Controls - slider always long and responsive */}
      {getCurrentHistory().length > 1 && (
        <Paper
          elevation={4}
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: { xs: 8, sm: 16, md: 24 },
            transform: 'translateX(-50%)',
            zIndex: 20,
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 1, sm: 1.5, md: 2 },
            backgroundColor: 'rgba(255,255,255,0.98)',
            borderRadius: 999,
            boxShadow: 6,
            minWidth: 400,
            maxWidth: 700,
            width: { xs: 400, sm: 500, md: 600 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <IconButton
            onClick={handleRewind}
            disabled={playbackIndex === 0}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.07)',
              borderRadius: 999,
              mx: 1,
            }}
            size="large"
          >
            <FastRewindIcon fontSize="medium" />
          </IconButton>
          <IconButton
            onClick={() => setPlaying(p => !p)}
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              borderRadius: 999,
              mx: 1,
              width: 48,
              height: 48,
              '&:hover': { backgroundColor: 'primary.dark' },
            }}
            size="large"
          >
            {playing ? <PauseIcon fontSize="large" /> : <PlayArrowIcon fontSize="large" />}
          </IconButton>
          <IconButton
            onClick={handleFastForward}
            disabled={playbackIndex === getCurrentHistory().length - 1}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.07)',
              borderRadius: 999,
              mx: 1,
            }}
            size="large"
          >
            <FastForwardIcon fontSize="medium" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 200, maxWidth: 500, px: 2 }}>
            <Slider
              min={0}
              max={getCurrentHistory().length > 1 ? getCurrentHistory().length - 1 : 1}
              value={playbackIndex}
              onChange={handleSliderChange}
              step={1}
              marks={getCurrentHistory().length <= 10 ? getCurrentHistory().map((_, i) => ({ value: i })) : false}
              track={false}
              sx={{
                width: '100%',
                height: 10,
                borderRadius: 5,
                background: 'transparent',
                '& .MuiSlider-rail': {
                  opacity: 1,
                  backgroundColor: '#e0e0e0',
                  height: 10,
                  borderRadius: 5,
                },
                '& .MuiSlider-track': {
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                  height: 10,
                  borderRadius: 5,
                  boxShadow: '0 0 8px #1976d2',
                },
                '& .MuiSlider-thumb': {
                  width: 24,
                  height: 24,
                  background: 'linear-gradient(135deg, #1976d2 60%, #42a5f5 100%)',
                  boxShadow: '0 0 12px #1976d2',
                  border: '3px solid white',
                  transition: 'box-shadow 0.2s',
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: '0 0 20px #1976d2',
                  },
                },
                '& .MuiSlider-mark': {
                  backgroundColor: '#bdbdbd',
                  height: 10,
                  width: 2,
                  borderRadius: 1,
                },
                '& .MuiSlider-markActive': {
                  backgroundColor: '#1976d2',
                },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main', minWidth: 70, textAlign: 'center' }}>
            {currentTime}
          </Typography>
        </Paper>
      )}

      {/* No Data Message - only show if truly no data for the selected mode */}
      {((playbackMode === 'user' && locationHistory.length === 0) || (playbackMode === 'group' && groupLocationHistory.length === 0)) && noDataMessage && (
        <Paper
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 30,
            px: 3,
            py: 2,
            backgroundColor: 'rgba(255,255,255,0.97)',
            borderRadius: 3,
            boxShadow: 2,
            minWidth: 220,
            maxWidth: 400,
            textAlign: 'center',
            fontSize: 16,
          }}
        >
          <Typography color="text.secondary" align="center">
            {noDataMessage}
          </Typography>
        </Paper>
      )}

      {/* Settings Dialog */}
      <Dialog 
        open={settingsOpen} 
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Playback Settings</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Playback Mode</InputLabel>
            <Select
              value={playbackMode}
              label="Playback Mode"
              onChange={e => setPlaybackMode(e.target.value as 'user' | 'group')}
            >
              <MenuItem value="user">User Playback</MenuItem>
              <MenuItem value="group">Group Playback</MenuItem>
            </Select>
          </FormControl>

          {playbackMode === 'user' && users.length > 0 && (
              <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUser}
                label="User"
                onChange={e => setSelectedUser(e.target.value)}
              >
                <MenuItem value="all">All Users</MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.username} ({u.user_group})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {playbackMode === 'group' && (
              <FormControl fullWidth>
              <InputLabel>Group</InputLabel>
              <Select
                value={selectedGroup}
                label="Group"
                onChange={e => setSelectedGroup(e.target.value)}
              >
                {VALID_USER_GROUPS.map(group => (
                  <MenuItem key={group} value={group}>
                    {group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl fullWidth>
            <InputLabel>Date</InputLabel>
            <TextField
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              label="Date"
              fullWidth
            />
          </FormControl>

          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Map Style Switcher */}
      <Box sx={{ position: 'fixed', bottom: 120, right: 24, zIndex: 30 }}>
        <Paper elevation={6} sx={{ borderRadius: 3, p: 1, display: 'flex', flexDirection: 'column', gap: 1, background: 'rgba(255,255,255,0.95)' }}>
          <Button variant={mapStyle==='map'?'contained':'outlined'} size="small" onClick={()=>setMapStyle('map')}>Map</Button>
          <Button variant={mapStyle==='satellite'?'contained':'outlined'} size="small" onClick={()=>setMapStyle('satellite')}>Satellite</Button>
          <Button variant={mapStyle==='hybrid'?'contained':'outlined'} size="small" onClick={()=>setMapStyle('hybrid')}>Hybrid</Button>
        </Paper>
      </Box>
    </>
  );
} 